import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import swal from 'sweetalert';
import { Card } from 'semantic-ui-react';
import moment from 'moment';
import { formatMoneyLegacy } from '../../utils/currency';
import { showPackage, updatePackage, deletePackage, updatePublish } from '../../api/packageApi';
import { getAllPartners } from '../../api/partnerApi';
import { createServiceTransaction } from '../../api/serviceTransactionApi';
import history from '../../history';
import CreatorBadge from './CreatorBadge';

const PAYMENT_METHODS = [
	{ value: 'offline_bank', label: 'Bank transfer' },
	{ value: 'offline_cash', label: 'Cash' },
	{ value: 'offline_cheque', label: 'Cheque' }
];

class PackageDetails extends Component {
	state = {
		aPackage: null,
		fetchError: false,
		updating: false,
		assignVendorOpen: false,
		assignVendorForm: { vendor_id: '' },
		partners: [],
		recordPaymentOpen: false,
		recordPaymentForm: { amount: '', payment_mode: 'offline_bank', receipt_no: '', remarks: '' },
		recording: false
	};

	fetchPackage = () => {
		const idx = this.props.match.params.idx;
		this.setState({ fetchError: false });
		showPackage(idx)
			.then((r) => this.setState({ aPackage: r.data, fetchError: false }))
			.catch(() => this.setState({ aPackage: null, fetchError: true }));
	};

	componentDidMount() {
		const idx = this.props.match.params.idx;
		const fromState = this.props.location.state && (this.props.location.state.aPackage || this.props.location.state.package);
		if (fromState && (fromState.idx || fromState.id) === idx) {
			this.setState({ aPackage: fromState });
			this.fetchPackage();
			return;
		}
		this.fetchPackage();
	}

	destroy = (id) => {
		swal({ title: 'Are you sure?', text: 'This private tour will be deleted.', icon: 'warning', buttons: true, dangerMode: true })
			.then((ok) => {
				if (ok) {
					deletePackage(id)
						.then(() => { swal('Deleted', { icon: 'success' }); history.push('/admin/manage_private_tours'); })
						.catch(() => swal('Delete failed', { icon: 'error' }));
				}
			});
	};

	publish = (shouldPublish) => {
		const pkg = this.state.aPackage;
		if (!pkg) return;
		swal({ title: 'Are you sure?', text: `Publish/unpublish this private tour?`, icon: 'warning', buttons: true, dangerMode: true })
			.then((ok) => {
				if (ok) {
					updatePublish(pkg.idx, shouldPublish)
						.then(() => { swal(`Private tour ${shouldPublish ? 'published' : 'unpublished'}`, { icon: 'success' }); this.fetchPackage(); })
						.catch(() => swal('Update failed', { icon: 'error' }));
				}
			});
	};

	openAssignVendor = () => {
		getAllPartners({ q: { status_eq: 'approved', for_assignment_type: 'PartnerService' } })
			.then((r) => {
				const list = (r.data && (Array.isArray(r.data) ? r.data : r.data.partners || r.data.vendors)) || [];
				this.setState({ assignVendorOpen: true, partners: list, assignVendorForm: { vendor_id: this.state.aPackage?.vendor_id || '' } });
			})
			.catch(() => this.setState({ assignVendorOpen: true, partners: [] }));
	};
	closeAssignVendor = () => this.setState({ assignVendorOpen: false });
	assignVendorSubmit = () => {
		const { aPackage, assignVendorForm } = this.state;
		if (!aPackage || !assignVendorForm.vendor_id) {
			swal('Select a vendor', 'Vendor is required.', 'info');
			return;
		}
		if (aPackage.vendor_id && assignVendorForm.vendor_id.toString() !== aPackage.vendor_id.toString()) {
			swal('Cannot reassign', 'This listing is already assigned to a vendor.', 'info');
			return;
		}
		this.setState({ updating: true });
		updatePackage(aPackage.idx, { vendor_id: assignVendorForm.vendor_id })
			.then(() => {
				swal('Assigned', 'Vendor assigned. They will see this in My Private Tours.', 'success');
				this.closeAssignVendor();
				this.fetchPackage();
			})
			.catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Assign failed', 'error'))
			.finally(() => this.setState({ updating: false }));
	};

	openRecordPayment = () => {
		const pkg = this.state.aPackage;
		const payout = (pkg && (pkg.vendor_payout != null && pkg.vendor_payout > 0 ? pkg.vendor_payout : pkg.vendor_total_price));
		const amount = payout != null ? String(Number(payout).toFixed(0)) : '';
		this.setState({ recordPaymentOpen: true, recordPaymentForm: { amount, payment_mode: 'offline_bank', receipt_no: '', remarks: '' } });
	};
	closeRecordPayment = () => this.setState({ recordPaymentOpen: false });
	recordPaymentSubmit = () => {
		const { aPackage, recordPaymentForm } = this.state;
		const amount = Number(recordPaymentForm.amount);
		if (!aPackage.vendor_id || !amount || amount <= 0) {
			swal('Invalid', 'Amount is required and must be greater than 0.', 'info');
			return;
		}
		this.setState({ recording: true });
		createServiceTransaction({
			vendor_id: aPackage.vendor_id,
			direction: 'debit',
			amount,
			assignment_type: 'Package',
			assignment_idx: aPackage.idx,
			payment_mode: recordPaymentForm.payment_mode,
			receipt_no: recordPaymentForm.receipt_no || undefined,
			remarks: recordPaymentForm.remarks || undefined
		})
			.then(() => {
				swal('Recorded', 'Payment recorded for this private tour. Vendor balance updated.', 'success');
				this.closeRecordPayment();
				this.fetchPackage();
			})
			.catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed to record payment', 'error'))
			.finally(() => this.setState({ recording: false }));
	};

	render() {
		const { aPackage, updating, fetchError } = this.state;
		if (fetchError) {
			return (
				<div className="container p-4">
					<div className="alert alert-warning">Private tour not found or it has been deleted.</div>
					<Link to="/admin/manage_private_tours" className="btn btn-outline-primary">Back to Private Tours</Link>
				</div>
			);
		}
		if (!aPackage || !aPackage.idx) return <div className="container p-4">Loading…</div>;

		const cc = aPackage.currency_code || 'NPR';
		const money = (x) => (x != null && x !== '' ? formatMoneyLegacy(x, cc) : '—');
		const pct = (x) => (x != null ? `${Number(x)}%` : '—');
		const approvalStatus = aPackage.approval_status || 'pending';

		return (
			<div className="container">
				<Card fluid>
					<Card.Content>
						<div className="d-flex justify-content-between flex-wrap">
							<h3 className="title">
								{aPackage.name || 'Private Tour'}
								<CreatorBadge type={aPackage.creator_type} label={aPackage.creator_label} />
							</h3>
							<div>
								{aPackage.has_any_inquiry_or_booking ? (
									<span className="text-muted small" title="Not available once anyone has inquired or booked">Edit / Delete locked</span>
								) : (
									<>
										<Link to={{ pathname: `/admin/private_tour_form/${aPackage.idx}`, state: { privateTour: aPackage } }} className="btn btn-outline-primary">Edit</Link>
										<span className="btn btn-outline-danger ml-2" onClick={() => this.destroy(aPackage.idx)}>Delete</span>
									</>
								)}
							</div>
						</div>
						<Link to="/admin/manage_private_tours" className="btn btn-outline-secondary btn-sm mt-2 mb-3">← Back to Private Tours</Link>

						{approvalStatus === 'pending' && (
							<div className="alert alert-warning mb-3">Pending approval. Approve or reject from the vendor listings table.</div>
						)}
						{approvalStatus === 'rejected' && aPackage.rejection_reason && (
							<div className="alert alert-danger mb-3">Rejected: {aPackage.rejection_reason}</div>
						)}

						<div className="mb-3">
							<strong>Vendor (partner)</strong>
							{aPackage.vendor_id ? (
								<>
									<span className="ml-2">Assigned to {aPackage.vendor_company_name || `ID ${aPackage.vendor_id}`}{(aPackage.vendor_payout != null && aPackage.vendor_payout > 0) ? ` · Payout ${money(aPackage.vendor_payout)}` : ''}</span>
									<p className="text-muted small mt-1 mb-0">This listing belongs to the assigned vendor.</p>
									<div className="mt-2 p-2 bg-light rounded small">
										<strong>This private tour:</strong> Final price <strong>{money(aPackage.final_price)}</strong>
										{(aPackage.vendor_payout != null && aPackage.vendor_payout > 0) && <> · Vendor payout <strong>{money(aPackage.vendor_payout)}</strong></>}
										<> · Paid to vendor for this tour <strong>{aPackage.vendor_paid_total != null ? money(aPackage.vendor_paid_total) : money(0)}</strong></>
									</div>
									{(aPackage.vendor_payout != null && aPackage.vendor_paid_total != null && Number(aPackage.vendor_paid_total) >= Number(aPackage.vendor_payout)) ? (
										<span className="badge badge-success mt-1">Paid</span>
									) : (
										<button type="button" className="btn btn-outline-success btn-sm mt-1" onClick={this.openRecordPayment} disabled={!!updating || !!this.state.recording}>Record payment to vendor (for this tour)</button>
									)}
									<p className="text-muted small mt-1 mb-0">Or pay from <Link to="/admin/summaries">Vendor Payouts</Link>.</p>
								</>
							) : (
								<>
									<span className="ml-2 text-muted">Not assigned</span>
									<button type="button" className="btn btn-outline-primary btn-sm ml-2" onClick={this.openAssignVendor} disabled={!!updating}>Assign vendor</button>
								</>
							)}
						</div>

						<div className="list-view mt-3">
							<div className="list"><span className="label">Approval status</span><span className="value"><span className={`badge badge-${approvalStatus === 'approved' ? 'success' : approvalStatus === 'rejected' ? 'danger' : 'warning'}`}>{approvalStatus}</span></span></div>
							<div className="list"><span className="label">From → To</span><span className="value">{(aPackage.origin || '—')} → {(aPackage.destination || '—')}</span></div>
							<div className="list"><span className="label">Vendor price</span><span className="value">{money(aPackage.vendor_total_price)}</span></div>
							<div className="list"><span className="label">Markup %</span><span className="value">{pct(aPackage.markup_percentage)}</span></div>
							<div className="list"><span className="label">Final price</span><span className="value">{money(aPackage.final_price)}</span></div>
							<div className="list"><span className="label">Platform margin</span><span className="value">{money(aPackage.platform_margin)}</span></div>
							<div className="list"><span className="label">Vendor payout</span><span className="value">{money(aPackage.vendor_payout)}</span></div>
							<div className="list"><span className="label">Published</span><span className="value">{String(!!aPackage.published)}</span></div>
						</div>

						{approvalStatus === 'approved' && (
							<div className="mt-3">
								{!aPackage.published && (
									<button type="button" className="btn btn-outline-primary btn-sm" onClick={() => this.publish(true)}>Publish private tour</button>
								)}
								{aPackage.published && (
									<button type="button" className="btn btn-outline-danger btn-sm ml-1" onClick={() => this.publish(false)}>Unpublish private tour</button>
								)}
							</div>
						)}

						<div className="mt-4">
							<Link to="/admin/manage_private_tours" className="btn btn-outline-primary">Manage Private Tours (inquiries & bookings)</Link>
						</div>

						{(aPackage.bookings && aPackage.bookings.length > 0) && (
							<div className="mt-4">
								<h5 className="mb-2">Bookings</h5>
								<p className="text-muted small">Customers who have booked or inquired about this private tour. Use <strong>View</strong> to open the inquiry and send payment link or update status.</p>
								<div className="table-responsive">
									<table className="table table-sm table-bordered">
										<thead>
											<tr>
												<th>Name</th>
												<th>Email</th>
												<th>Phone / WhatsApp</th>
												<th>#</th>
												<th>Amount</th>
												<th>Status</th>
												<th>Dates</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{aPackage.bookings.map((b) => (
												<tr key={b.idx}>
													<td>{b.customer_name || '—'}</td>
													<td>{b.customer_email || '—'}</td>
													<td>{b.customer_phone ? <a href={`tel:${b.customer_phone.replace(/\s/g, '')}`}>{b.customer_phone}</a> : '—'}</td>
													<td>{b.number_of_people != null ? b.number_of_people : '—'}</td>
													<td>{b.amount != null ? money(b.amount) : '—'}</td>
													<td><span className={`badge badge-${b.status === 'verified' ? 'success' : b.status === 'pending' ? 'warning' : 'secondary'}`}>{b.status || '—'}</span></td>
													<td>{(b.pickup_date || b.start_date) ? moment(b.pickup_date || b.start_date).format('D MMM YYYY') : '—'}</td>
													<td>
														{b.inquiry_idx && (
															<Link to={`/admin/inquiry_details/${b.inquiry_idx}`} className="btn btn-outline-secondary btn-sm">View</Link>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						)}

						{(aPackage.addons && aPackage.addons.length > 0) && (
							<div className="mt-4">
								<h5 className="mb-2">Addons</h5>
								{aPackage.addons.map((addon) => (
									<span key={addon.id || addon.idx} className="ui label mr-1" title={addon.description}>{addon.name}</span>
								))}
							</div>
						)}

						{aPackage.description && (
							<div className="mt-4">
								<h5 className="mb-2">Description</h5>
								<div className="text-muted" dangerouslySetInnerHTML={{ __html: aPackage.description }} />
							</div>
						)}

						{(aPackage.inclusions || aPackage.exclusions) && (
							<div className="row mt-4">
								{aPackage.inclusions && <div className="col-6"><h5 className="mb-2">Inclusions</h5><div className="text-muted">{aPackage.inclusions}</div></div>}
								{aPackage.exclusions && <div className="col-6"><h5 className="mb-2">Exclusions</h5><div className="text-muted">{aPackage.exclusions}</div></div>}
							</div>
						)}
					</Card.Content>
				</Card>

				{this.state.assignVendorOpen && (
					<div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
						<div className="modal-dialog modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">Assign vendor</h5>
									<button type="button" className="close" onClick={this.closeAssignVendor}><span>&times;</span></button>
								</div>
								<div className="modal-body">
									<p className="text-muted small">Assign a partner to this private tour. They will see it under My Private Tours.</p>
									{aPackage.vendor_payout != null && aPackage.vendor_payout > 0 && (
										<p className="mb-2"><strong>Vendor payout: {money(aPackage.vendor_payout)}</strong></p>
									)}
									<div className="form-group">
										<label>Vendor (partner)</label>
										<select className="form-control" value={this.state.assignVendorForm.vendor_id} onChange={(e) => this.setState({ assignVendorForm: { ...this.state.assignVendorForm, vendor_id: e.target.value } })}>
											<option value="">Select vendor</option>
											{(() => {
												const { aPackage, partners } = this.state;
												const creatorVendor = aPackage && aPackage.created_by_user_id != null && partners.length > 0
													? partners.find((p) => p.user_id != null && String(p.user_id) === String(aPackage.created_by_user_id))
													: null;
												const otherVendors = creatorVendor ? partners.filter((p) => p.id !== creatorVendor.id) : partners;
												return (
													<>
														{creatorVendor && (
															<optgroup label="Creator Vendor">
																<option value={creatorVendor.id}>{creatorVendor.company_name || `${(creatorVendor.first_name || '')} ${(creatorVendor.last_name || '')}`.trim() || `Vendor ${creatorVendor.id}`}</option>
															</optgroup>
														)}
														<optgroup label={creatorVendor ? 'Other Approved Vendors' : 'Vendors'}>
															{otherVendors.map((p) => (
																<option key={p.id} value={p.id}>{p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Vendor ${p.id}`}</option>
															))}
														</optgroup>
													</>
												);
											})()}
										</select>
									</div>
								</div>
								<div className="modal-footer">
									<button type="button" className="btn btn-secondary" onClick={this.closeAssignVendor}>Cancel</button>
									<button type="button" className="btn btn-primary" onClick={this.assignVendorSubmit} disabled={!!updating || !this.state.assignVendorForm.vendor_id}>Assign</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{this.state.recordPaymentOpen && (
					<div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
						<div className="modal-dialog modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">Record payment to vendor (this tour)</h5>
									<button type="button" className="close" onClick={this.closeRecordPayment}><span>&times;</span></button>
								</div>
								<div className="modal-body">
									<p className="text-muted small">This payment will be linked to this private tour. Vendor payout: {money(aPackage.vendor_payout)}.</p>
									<div className="form-group">
										<label>Amount *</label>
										<input type="number" min={0.01} step={0.01} className="form-control" value={this.state.recordPaymentForm.amount} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, amount: e.target.value } })} placeholder="0" />
									</div>
									<div className="form-group">
										<label>Payment method</label>
										<select className="form-control" value={this.state.recordPaymentForm.payment_mode} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, payment_mode: e.target.value } })}>
											{PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
										</select>
									</div>
									<div className="form-group">
										<label>Receipt / Reference no.</label>
										<input type="text" className="form-control" value={this.state.recordPaymentForm.receipt_no} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, receipt_no: e.target.value } })} placeholder="Optional" />
									</div>
									<div className="form-group">
										<label>Remarks</label>
										<input type="text" className="form-control" value={this.state.recordPaymentForm.remarks} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, remarks: e.target.value } })} placeholder="Optional" />
									</div>
								</div>
								<div className="modal-footer">
									<button type="button" className="btn btn-secondary" onClick={this.closeRecordPayment}>Cancel</button>
									<button type="button" className="btn btn-primary" onClick={this.recordPaymentSubmit} disabled={!!this.state.recording || !this.state.recordPaymentForm.amount}>Record payment</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}
}

export default PackageDetails;
